import { Component, DEFAULT_CURRENCY_CODE, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { DESTINATION_CURRENCY_KEY } from '@lx/currency/shared/model/currency.model';
import { QuoteOutput, QuoteTotalLayouts } from '@lx/quote-tool/shared/types';
import { Amount } from '@lx/shared/data-access/types';
import { DateFormat } from '@lx/shared/i18n';
import { getDateFromString, isDateAfter } from '@lx/shared/utils';

@Component({
  selector: 'lx-quote-total-price',
  templateUrl: './quote-total-price.component.html',
  styleUrls: ['./quote-total-price.component.scss'],
})
export class QuoteTotalPriceComponent implements OnChanges {
  @Input() layout: QuoteTotalLayouts | undefined;
  @Input() quote: QuoteOutput | undefined;
  @Input() quoteKey = '';
  @Input() activeQuoteSlot = 1;
  @Input() selectedCurrency: string | undefined;
  @Input() hasCurrencySelector = false;
  @Input() displayDestinationCurrency = false;
  @Input() filterVisibleCurrencies = false;
  @Input() hasDisclaimerSection = false;
  @Input() showDecimals = false;
  @Input() infoDisclaimer: string | undefined;
  @Input() showBothCurrencies = false;
  @Output() viewDetails = new EventEmitter<void>();
  @Output() currencyChanged = new EventEmitter<string>();

  protected destinationCurrency: string;
  protected currency: string;
  protected visibleCurrencies: Amount[];
  protected QuoteTotalLayouts = QuoteTotalLayouts;
  protected decimalPlaces: number;
  protected dateFormat = DateFormat.SHORT_MONTH_NAME;
  protected earliestValidityDate: Date | undefined;

  private defaultCurrencyCode = inject(DEFAULT_CURRENCY_CODE);

  ngOnChanges({ quote, selectedCurrency }: SimpleChanges): void {
    if (quote && quote.currentValue && quote.currentValue !== quote.previousValue) {
      this.destinationCurrency = this.quote.input?.destinationCurrency;
      this.visibleCurrencies = this.filterVisibleCurrencies ? this.quote.grandTotal?.filter((amount) => amount.visible) : this.quote.grandTotal;
      this.earliestValidityDate = this.calculateEarliestValidityDate();
    }

    if (!this.currency || selectedCurrency?.currentValue) {
      this.currency = (this.selectedCurrency === DESTINATION_CURRENCY_KEY ? this.destinationCurrency : this.selectedCurrency) ?? this.defaultCurrencyCode;
      if (this.visibleCurrencies?.length && !this.visibleCurrencies?.some((amount) => amount.currency === this.currency)) {
        this.currency = this.visibleCurrencies[0]?.currency;
      }
    }

    // quoting in local currency overrides any other setting
    if (this.quote?.singleCurrencyMode) {
      this.currency = this.quote?.displayCurrency || this.selectedCurrency;
    }

    this.decimalPlaces = this.showDecimals ? 2 : 0;
  }

  onCurrencyChange(newCurrency: string) {
    if (this.quote?.singleCurrencyMode) {
      // we do not allow any other currency rather than display currency on single currency mode
      this.currency = this.quote?.displayCurrency || this.selectedCurrency;
      return;
    }

    if (this.currency !== newCurrency) {
      this.currency = newCurrency;
      this.currencyChanged.emit(newCurrency);
    }
  }

  showCurrencyDisclaimer(): boolean {
    return this.displayDestinationCurrency && this.destinationCurrency && this.currency !== this.destinationCurrency;
  }

  isSelectedSlot(slot: string) {
    return slot === this.activeQuoteSlot.toString();
  }

  private calculateEarliestValidityDate(): Date | undefined {
    if (!this.quote?.marketDiscountValidityDate) {
      return undefined;
    }

    const marketDiscountDate = getDateFromString(this.quote.marketDiscountValidityDate);
    const earliestDiscountDate = this.getEarliestDiscountExpirationDate();

    if (!earliestDiscountDate) {
      return marketDiscountDate;
    }

    return isDateAfter(earliestDiscountDate, marketDiscountDate) ? marketDiscountDate : earliestDiscountDate;
  }

  private getEarliestDiscountExpirationDate(): Date | undefined {
    const discountsWithDates = this.quote?.discounts?.filter((d) => d.expirationDate);

    if (!discountsWithDates?.length) {
      return undefined;
    }

    const earliestDiscount = discountsWithDates.reduce((previous, current) => {
      const previousDate = getDateFromString(previous.expirationDate);
      const currentDate = getDateFromString(current.expirationDate);
      return isDateAfter(currentDate, previousDate) ? previous : current;
    });

    return getDateFromString(earliestDiscount.expirationDate);
  }
}
